import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import {
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { Search } from "@mui/icons-material";

import StyledCard from "../../../shared/general/Card";

import utils from "../../../../../utils";

const Header = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
});

const InputSearch = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "4px 0 0 4px",
    "& input": {
      height: "14px",
    },
  },
});

const ButtonSearch = styled(Button)({
  height: "100%",
  backgroundColor: "#007afe",
  borderRadius: "0 4px 4px 0",
  "&:hover": {
    backgroundColor: "#1565c0",
  },
});

const Body = styled("div")({
  marginTop: "20px",
});

const OverviewVendor = ({ company }) => {
  const { t } = useTranslation();

  const { result: vendorContract } = useSelector(
    (state) => state.vendorContract
  );
  const [search, setSearch] = useState("");

  const filteredContract = useMemo(() => {
    if (!vendorContract) return [];
    const keyword = search.trim().toLowerCase();
    return vendorContract.filter((item) => {
      const matchCompany = !company || item.idCompany === company;
      const matchKeyword =
        !keyword ||
        (item.companyName || "").toLowerCase().includes(keyword) ||
        (item.companyNameShort || "").toLowerCase().includes(keyword);
      return matchCompany && matchKeyword;
    });
  }, [vendorContract, search, company]);

  const total = useMemo(
    () =>
      filteredContract.reduce(
        (acc, item) => ({
          numberSupply: acc.numberSupply + (item.numberEmployee || 0),
          expensesMonth: acc.expensesMonth + (item.expensesMonth || 0),
          revenueMonth: acc.revenueMonth + (item.revenueMonth || 0),
          differenceMonth: acc.differenceMonth + (item.differenceMonth || 0),
          expensesYear: acc.expensesYear + (item.expensesYear || 0),
          revenueYear: acc.revenueYear + (item.revenueYear || 0),
          differenceYear: acc.differenceYear + (item.differenceYear || 0),
        }),
        {
          numberSupply: 0,
          expensesMonth: 0,
          revenueMonth: 0,
          differenceMonth: 0,
          expensesYear: 0,
          revenueYear: 0,
          differenceYear: 0,
        }
      ),
    [filteredContract]
  );

  const SymbolSyncRaise = (value) => {
    if (value === undefined || value === null) return null;
    if (value) {
      return (
        <i className="fa-solid fa-caret-up" style={{ color: "#2e7d32" }} />
      );
    }
    return <i className="fa-solid fa-caret-down" style={{ color: "#d32f2f" }} />;
  };

  return (
    <div
      style={{
        height: "fit-content",
        marginTop: "20px",
        paddingBottom: "20px",
      }}
    >
      <Header>
        <div style={{ display: "flex", alignItems: "center" }}>
          <InputSearch
            variant="outlined"
            placeholder={t("Search")}
            defaultValue={search}
            onBlur={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") setSearch(event.target.value);
            }}
          />
          <ButtonSearch>
            <Search style={{ color: "white" }} />
          </ButtonSearch>
        </div>
      </Header>
      <Body>
        <StyledCard>
          <TableContainer style={{ height: "600px", maxHeight: "600px" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={{ minWidth: "320px", fontSize: "bold" }}>
                    {t("Customer")}
                  </TableCell>
                  <TableCell
                    style={{ minWidth: "150px", fontSize: "bold" }}
                    align="center"
                  >
                    {t("Amount")} ({t("Person")})
                  </TableCell>
                  <TableCell
                    style={{ minWidth: "150px", fontSize: "bold" }}
                    align="center"
                  >
                    {t("Expense")} ({t("Month")})
                  </TableCell>
                  <TableCell
                    style={{ minWidth: "150px", fontSize: "bold" }}
                    align="center"
                  >
                    {t("Income")} ({t("Month")})
                  </TableCell>
                  <TableCell
                    style={{ minWidth: "150px", fontSize: "bold" }}
                    align="center"
                  >
                    {t("Difference")} ({t("Month")})
                  </TableCell>
                  <TableCell
                    style={{ minWidth: "150px", fontSize: "bold" }}
                    align="center"
                  >
                    {t("TotalExpenses")} ({t("Year")})
                  </TableCell>
                  <TableCell
                    style={{ minWidth: "150px", fontSize: "bold" }}
                    align="center"
                  >
                    {t("TotalIncome")} ({t("Year")})
                  </TableCell>
                  <TableCell
                    style={{ minWidth: "150px", fontSize: "bold" }}
                    align="center"
                  >
                    {t("Difference")} ({t("Year")})
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredContract && filteredContract.length > 0 ? (
                  filteredContract.map((item) => {
                    // บริษัทที่ไม่มีพนักงาน (0 คน) → ขึ้นสีแดงทั้งแถว
                    const isNoEmployee = !item.numberEmployee;
                    return (
                    <TableRow
                      key={item.idCompany}
                      style={{
                        height: "53px",
                        backgroundColor: isNoEmployee ? "#fdecea" : undefined,
                      }}
                    >
                      <TableCell>
                        <Link
                          href={`/vendor/employees?filter=${item.idCompany}`}
                          style={isNoEmployee ? { color: "#d32f2f" } : undefined}
                        >
                          {item.companyName}
                          {` (${item.companyNameShort})`}
                        </Link>
                      </TableCell>
                      <TableCell align="center">
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              marginRight: "10px",
                              color: isNoEmployee ? "#d32f2f" : undefined,
                              fontWeight: isNoEmployee ? 700 : undefined,
                            }}
                          >
                            {utils.numberWithCommasWithoutDot(
                              item.numberEmployee ? item.numberEmployee : 0
                            )}
                          </div>
                          {SymbolSyncRaise(item.isSupplyRise)}
                        </div>
                      </TableCell>
                      <TableCell align="center">
                        {utils.numberWithCommas(
                          item.expensesMonth ? item.expensesMonth : 0
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {utils.numberWithCommas(
                          item.revenueMonth ? item.revenueMonth : 0
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div style={{ marginRight: "10px" }}>
                            {utils.numberWithCommas(
                              item.differenceMonth ? item.differenceMonth : 0
                            )}
                          </div>
                          {SymbolSyncRaise(item.isDifferenceMRaise)}
                        </div>
                      </TableCell>
                      <TableCell align="center">
                        {utils.numberWithCommas(
                          item.expensesYear ? item.expensesYear : 0
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {utils.numberWithCommas(
                          item.revenueYear ? item.revenueYear : 0
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div style={{ marginRight: "10px" }}>
                            {utils.numberWithCommas(
                              item.differenceYear ? item.differenceYear : 0
                            )}
                          </div>
                          {SymbolSyncRaise(item.isDifferenceYRaise)}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} style={{ borderBottom: "0px" }}>
                      <Typography variant="h6" style={{ textAlign: "center" }}>
                        {t("NoCompanyList")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {/* {AllCompanys &&
                  AllCompanys.length < 9 &&
                  [
                    ...Array(
                      AllCompanys.length > 0
                        ? 9 - AllCompanys.length
                        : 10 - AllCompanys.length
                    ),
                  ].map((item, index) => (
                    <TableRow style={{ height: "53px" }} key={index}>
                      <TableCell
                        style={{ borderWidth: "0px" }}
                        colSpan={8}
                      ></TableCell>
                    </TableRow>
                  ))} */}
                <TableRow
                  style={{
                    bottom: 0,
                    position: "sticky",
                    backgroundColor: "white",
                  }}
                >
                  <TableCell
                    style={{
                      color: "black",
                      borderTop: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    Total
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
                  >
                    {utils.numberWithCommasWithoutDot(total.numberSupply)}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
                  >
                    {utils.numberWithCommas(total.expensesMonth)}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
                  >
                    {utils.numberWithCommas(total.revenueMonth)}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
                  >
                    {utils.numberWithCommas(total.differenceMonth)}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
                  >
                    {utils.numberWithCommas(total.expensesYear)}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
                  >
                    {utils.numberWithCommas(total.revenueYear)}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
                  >
                    {utils.numberWithCommas(total.differenceYear)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </StyledCard>
      </Body>
    </div>
  );
};

export default OverviewVendor;
